import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PauseCircle, PlayCircle, Loader2, X } from 'lucide-react';
import { Switch } from './ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-');

// Settings > Admin Tools: pause automated supplier emails + see what's queued next.
export default function SupplierEmailPausePanel() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/admin/supplier-email-pause`).then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    const next = !d.paused;
    if (!next && !window.confirm('Resume all automated and bulk emails? Anything held, including scheduled campaigns, will start sending.')) return;
    setBusy(true);
    try { const r = await axios.post(`${API}/admin/supplier-email-pause`, { paused: next }); setD(r.data); toast.success(next ? 'Supplier emails paused' : 'Supplier emails resumed'); }
    catch { toast.error('Could not update'); }
    finally { setBusy(false); }
  };
  const cancel = async (e) => {
    if (!window.confirm(`Cancel "${e.subject}" to ${e.to} for good?`)) return;
    await axios.delete(`${API}/admin/supplier-email-pause/outbox/${e.id}`); toast.success('Email cancelled'); load();
  };

  if (d === false) return null;
  return (
    <div className={`space-y-3 p-4 rounded-lg border ${d?.paused ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200'}`} data-testid="supplier-email-pause-panel">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          {d?.paused ? <PauseCircle className="w-4 h-4 text-amber-600" /> : <PlayCircle className="w-4 h-4 text-emerald-600" />}
          Automated &amp; bulk emails: <span className={d?.paused ? 'text-amber-700' : 'text-emerald-700'} data-testid="supplier-pause-state">{d ? (d.paused ? 'PAUSED' : 'Sending') : '…'}</span>
        </h3>
        {d ? <Switch checked={!!d.paused} disabled={busy} onCheckedChange={toggle} data-testid="supplier-pause-toggle" /> : <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
      </div>
      <p className="text-xs text-slate-400">When paused, every automated, scheduled and bulk email is held, for suppliers and care providers alike: bulk campaigns (including Send now), the buying-help series, webinar follow-ups and automations, sequences, unfinished-claim nudges and create-listing reminders. Still sent: 1:1 emails, automatic replies to listing enquiries, payment receipts and onboarding, team invites and team notifications.{d?.updated_by ? ` Last changed by ${d.updated_by}, ${fmt(d.updated_at)}.` : ''}</p>
      {d && (
        <div className="space-y-1.5" data-testid="supplier-upcoming">
          <p className="text-xs font-semibold text-slate-300">What's queued next</p>
          {d.campaigns.map((c) => (
            <div key={c.id} className="text-xs text-slate-300 flex justify-between gap-3 border-b border-slate-700/60 py-1">
              <span>Campaign: {c.name || c.subject}</span><span className="text-slate-500 shrink-0">{fmt(c.scheduled_at)}</span>
            </div>
          ))}
          {d.outbox.slice(0, 30).map((e) => (
            <div key={e.id} className="text-xs text-slate-300 flex justify-between items-center gap-3 border-b border-slate-700/60 py-1" data-testid={`supplier-upcoming-${e.id}`}>
              <span className="min-w-0 truncate">{e.subject} <span className="text-slate-500">→ {e.to}</span>{e.status === 'held_paused' && <span className="text-amber-700"> · held</span>}</span>
              <span className="flex items-center gap-2 shrink-0 text-slate-500">{fmt(e.send_after)}
                <button onClick={() => cancel(e)} title="Cancel this email" className="hover:text-red-400" data-testid={`supplier-cancel-${e.id}`}><X className="w-3.5 h-3.5" /></button></span>
            </div>
          ))}
          {d.outbox.length > 30 && <p className="text-[11px] text-slate-500">+ {d.outbox.length - 30} more scheduled emails</p>}
          {d.abandoned_claims_waiting > 0 && <p className="text-xs text-slate-400">{d.abandoned_claims_waiting} unfinished claims would get a "finish claiming" nudge.</p>}
          {!d.campaigns.length && !d.outbox.length && !d.abandoned_claims_waiting && <p className="text-xs text-slate-500" data-testid="supplier-upcoming-empty">Nothing scheduled to suppliers.</p>}
        </div>
      )}
    </div>
  );
}
